import { Subscription } from 'rxjs';
import { VideoProgressService } from '../../../services/video-progress.service';
import { SecureVideoService } from '../../../services/secure-video.service';
import { PlayerMode } from '../../../services/course-player.service';
import { environment } from '../../../../environments/environment';
import { parseBackendNumber } from '../../utils/backend-number.utils';

/**
 * Video state managed by the handler.
 * The parent component reads this state for template bindings.
 */
export interface VideoState {
  currentVideoTime: number;
  videoDuration: number;
  isVideoPlaying: boolean;
  videoWatchPercentage: number;
  videoCompleted: boolean;
  videoCompletedFromBackend: boolean;
  cumulativeWatchTime: number;
  lastTrackedTime: number;
  initialLastTrackedTime: number;
  videoElement: HTMLVideoElement | null;
  progressLoading: boolean;
  storedDuration?: number;
  secureVideoUrl: string;
  isLoadingVideo: boolean;
  videoEnded: boolean;
}

/**
 * Result of processing a video progress event.
 * Contains updated state fields and optional event data to emit.
 */
export interface VideoProgressResult {
  currentVideoTime: number;
  videoDuration: number;
  cumulativeWatchTime: number;
  lastTrackedTime: number;
  videoWatchPercentage: number;
  videoCompleted: boolean;
  rewound: boolean;
  progressEvent?: {
    currentTime: number;
    duration: number;
    percentage: number;
    cumulativeTime: number;
  };
}

/**
 * Dependencies injected from the parent component.
 * Using getter functions so the handler always reads current input values.
 */
export interface VideoProgressHandlerDeps {
  videoProgressService: VideoProgressService;
  secureVideoService: SecureVideoService;
  subscriptions: Subscription[];
  getModuleId: () => string | undefined;
  getCourseId: () => string | undefined;
  getVideoId: () => string | undefined;
  getVideoUrl: () => string;
  getMode: () => PlayerMode;
  getItemType: () => 'module' | 'unit_quiz';
  getRequiredWatchPercentage: () => number;
  getIsVideoRequired: () => boolean;
  /**
   * Whether the active module has a graded quiz. When true, completing the
   * video alone must NOT complete the module — the quiz submission is the gate
   * (KAN-238 regression guard).
   */
  getHasQuiz: () => boolean;
  onStateChanged?: () => void;
}

/**
 * Handles all video progress tracking logic extracted from SplitScreenPlayerComponent.
 * This is a plain class (not an Angular service) instantiated by the parent component.
 */
export class VideoProgressHandler {
  private state: VideoState;
  private deps: VideoProgressHandlerDeps;
  private progressSubscription?: Subscription;
  private currentLoadingModuleId: string | null = null;

  constructor(deps: VideoProgressHandlerDeps) {
    this.deps = deps;
    this.state = this.createDefaultState();
  }

  private createDefaultState(): VideoState {
    return {
      currentVideoTime: 0,
      videoDuration: 0,
      isVideoPlaying: false,
      videoWatchPercentage: 0,
      videoCompleted: false,
      videoCompletedFromBackend: false,
      cumulativeWatchTime: 0,
      lastTrackedTime: 0,
      initialLastTrackedTime: 0,
      videoElement: null,
      progressLoading: true,
      storedDuration: undefined,
      secureVideoUrl: '',
      isLoadingVideo: false,
      videoEnded: false
    };
  }

  /** Returns a copy of the current video state. */
  getState(): VideoState {
    return { ...this.state };
  }

  /** Partial update of the video state. */
  setState(partial: Partial<VideoState>): void {
    this.state = { ...this.state, ...partial };
  }

  /**
   * Reset all video state when switching modules.
   * Saves current progress before resetting.
   */
  async resetVideoState(): Promise<void> {
    if (this.deps.getModuleId()) {
      await this.deps.videoProgressService.stopTracking();
    }

    // Clear subscriptions
    this.deps.subscriptions.forEach(sub => sub.unsubscribe());
    this.deps.subscriptions.length = 0;

    this.state = {
      ...this.createDefaultState(),
      progressLoading: true
    };
  }

  /**
   * KAN-203: Synchronously clear per-module video DISPLAY state without
   * touching async backend calls or the loaded video URL.
   *
   * Why this exists separately from resetVideoState():
   *   `resetVideoState()` is async because it awaits stopTracking() (which
   *   flushes pending progress saves). When the parent's @Input moduleId
   *   changes, Angular re-renders the template SYNCHRONOUSLY before the async
   *   reset resolves. If we don't blank the displayed progress numbers right
   *   away, the parent template will bind stale values
   *   (`videoCompleted=true`, `cumulativeWatchTime=12`, etc.) from the
   *   previously-completed module to the bunny-player and quiz panel for the
   *   brief window before resetVideoState() completes — long enough for the
   *   user to see "Video Complete" on a fresh module and click Submit Quiz,
   *   triggering a confusing 400 from the backend (which correctly sees 0%).
   *
   * What this does NOT do:
   *   - Does NOT call any backend method (no stopTracking, no save, no fetch).
   *   - Does NOT clear `secureVideoUrl`. Unmounting the bunny-player is the
   *     job of `resetVideoState()` + the subsequent `loadNewVideo()`. We only
   *     blank the *progress numbers* the bunny-player and quiz panel read.
   *
   * Related: KAN-203 (https://kylewestendorfdev.atlassian.net/browse/KAN-203)
   */
  resetStateSync(): void {
    this.state = {
      ...this.state,
      currentVideoTime: 0,
      videoDuration: 0,
      isVideoPlaying: false,
      videoWatchPercentage: 0,
      videoCompleted: false,
      videoCompletedFromBackend: false,
      cumulativeWatchTime: 0,
      lastTrackedTime: 0,
      initialLastTrackedTime: 0,
      videoEnded: false,
      progressLoading: true,
      storedDuration: undefined
      // Intentionally NOT touched:
      //   - secureVideoUrl, isLoadingVideo: managed by resetVideoState() + loadNewVideo()
      //   - videoElement: cleared by resetVideoState() once stopTracking() flushes
    };
  }

  /**
   * Load existing video progress from the backend.
   * Handles cancellation of previous requests to prevent race conditions.
   */
  loadExistingProgress(): Promise<void> {
    if (this.progressSubscription) {
      this.progressSubscription.unsubscribe();
      this.state.progressLoading = false;
    }

    const moduleId = this.deps.getModuleId();
    if (!moduleId) {
      this.state.progressLoading = false;
      return Promise.resolve();
    }

    const courseId = this.deps.getCourseId();
    if (courseId === 'practice-mode') {
      this.state.progressLoading = false;
      return Promise.resolve();
    }

    if (this.deps.getItemType() === 'unit_quiz') {
      this.state.progressLoading = false;
      return Promise.resolve();
    }

    this.currentLoadingModuleId = moduleId;
    const loadingForModuleId = moduleId;

    return new Promise((resolve) => {
      this.progressSubscription = this.deps.videoProgressService
        .getVideoProgress(loadingForModuleId, courseId)
        .subscribe({
          next: (progress: any) => {
            if (loadingForModuleId !== this.deps.getModuleId()) {
              this.state.progressLoading = false;
              resolve();
              return;
            }

            if (progress) {
              // KAN-306 round 6 (CRITICAL, live dev): VideoProgressService's
              // getVideoProgress() now coerces every numeric field at its own
              // response boundary, but this callback also receives `progress`
              // as `any` and is a second, independent point where a
              // stringly-typed value (pg numerics serialize as strings) could
              // silently poison downstream arithmetic -- defense in depth,
              // not reliance on a single upstream fix.
              this.state.cumulativeWatchTime = parseBackendNumber(progress.furthestPosition, 0);
              this.state.videoWatchPercentage = parseBackendNumber(progress.watchPercentage, 0);
              this.state.videoCompletedFromBackend = progress.videoCompleted || false;
              this.state.videoCompleted = this.state.videoCompletedFromBackend ||
                (this.state.videoWatchPercentage >= this.deps.getRequiredWatchPercentage());
              this.state.lastTrackedTime = parseBackendNumber(progress.lastPosition, 0);
              this.state.initialLastTrackedTime = this.state.lastTrackedTime;
              this.state.storedDuration = parseBackendNumber(progress.duration, 0);
            }

            // When requiredWatchPercentage is 0, video is immediately satisfied.
            // Send a backend update to create the module_progress record so the
            // DB trigger can mark the module as complete. `updateVideoProgress`
            // emits `moduleCompleted$` on a non-null `completedAt`, which the
            // learner-modal subscribes to so the sidebar checkmark updates.
            if (this.deps.getRequiredWatchPercentage() === 0 && !this.state.videoCompletedFromBackend) {
              this.state.videoCompleted = true;
              const cId = this.deps.getCourseId();
              if (cId) {
                const service = this.deps.videoProgressService;
                // KAN-597: report the upsert's lifecycle so the footer shows a
                // pending state, not "Complete Module", until it settles.
                service
                  .trackCompletionSave(loadingForModuleId, service.updateVideoProgress(loadingForModuleId, {
                    courseId: cId,
                    lastPosition: 0,
                    furthestPosition: 0,
                    duration: this.state.storedDuration || 1,
                    forceComplete: false
                  }))
                  .subscribe({
                    next: () => {
                      this.state.videoCompletedFromBackend = true;
                      this.deps.onStateChanged?.();
                    },
                    error: (err: any) => {
                      console.error('[VideoProgressHandler] Failed to auto-complete 0% video requirement:', err);
                    }
                  });
              }
            }

            // KAN-238 follow-up: in instructor preview/test the sidebar checkmark is
            // driven SOLELY by `moduleCompleted$`. The 0%-auto-complete POST above only
            // fires on the FIRST preview (`!videoCompletedFromBackend`); once the backend
            // persists `videoCompleted:true`, that POST is skipped forever, so the signal
            // never re-fires and the checkmark stays empty on every later preview/reload.
            //
            // Decouple the preview completion signal from the POST: a no-watch-requirement
            // video (requiredWatchPercentage === 0) is satisfied simply by the instructor
            // landing on / advancing to it — that IS the in-session completion action — so
            // emit the signal directly on every load, regardless of whether the backend
            // already reports it complete. The modal then applies completion to its
            // ephemeral progress map (KAN-238).
            //
            // Scope is deliberately narrow:
            //   - Only the 0%-threshold case. A real watch-required video must be watched
            //     in-session; we must NOT mark it complete from a stale backend
            //     `videoCompleted:true` on preview open (instructor preview is "Test Mode"
            //     and starts fresh at 0% each open).
            //   - Only VIDEO-ONLY modules (no quiz). For a module with a graded quiz,
            //     completing the video must NOT complete the module — the quiz submission
            //     is the gate, exactly as for learners. Emitting here would mark the module
            //     (and potentially the whole course) complete with the quiz bypassed.
            //   - Only the active module (loadExistingProgress runs per active module), so
            //     this never pre-seeds other sidebar items on open.
            //   - Learner mode is excluded — it seeds `courseProgressMap` from backend
            //     `completed` flags on load and gets the signal from the real POST, so an
            //     extra emission here would be redundant and risk double-fires.
            const mode = this.deps.getMode();
            const isInstructorPreview = mode === 'instructor_preview' || mode === 'instructor_test';
            if (isInstructorPreview && this.deps.getRequiredWatchPercentage() === 0 && !this.deps.getHasQuiz()) {
              this.state.videoCompleted = true;
              this.deps.videoProgressService.moduleCompleted.next(loadingForModuleId);
            }

            this.state.progressLoading = false;
            resolve();
          },
          error: () => {
            this.state.progressLoading = false;
            resolve();
          }
        });
    });
  }

  /**
   * Process a video time update event.
   * Returns the updated state and any event data to emit.
   */
  handleVideoProgress(timeData: { currentTime: number; duration: number }): VideoProgressResult {
    this.state.currentVideoTime = timeData.currentTime;
    this.state.videoDuration = timeData.duration;

    let rewound = false;

    if (this.state.currentVideoTime < this.state.lastTrackedTime) {
      rewound = true;
      // KAN-306 round 2/3: reset the delta baseline to the new (lower)
      // position instead of leaving lastTrackedTime stuck at the historical
      // high mark. The backward jump itself still earns no credit -- but
      // WITHOUT this reset, once lastTrackedTime sat at a high mark (e.g.
      // ~duration after the video ended), every subsequent tick below it was
      // permanently misclassified as "rewind" forever, so cumulativeWatchTime
      // could never accrue again even when the learner played through
      // minutes of never-before-credited earlier content. Round 2 kept an
      // initialLastTrackedTime floor here (don't reset below where THIS
      // session started); round 3 removes it -- QA found a session that
      // RESUMES at/near the video's end has initialLastTrackedTime ~=
      // duration too, so every rewind landed below the floor and the freeze
      // recurred anyway.
      this.state.lastTrackedTime = this.state.currentVideoTime;
      return {
        currentVideoTime: this.state.currentVideoTime,
        videoDuration: this.state.videoDuration,
        cumulativeWatchTime: this.state.cumulativeWatchTime,
        lastTrackedTime: this.state.lastTrackedTime,
        videoWatchPercentage: this.state.videoWatchPercentage,
        videoCompleted: this.state.videoCompleted,
        rewound: true,
        progressEvent: {
          currentTime: timeData.currentTime,
          duration: timeData.duration,
          percentage: this.state.videoWatchPercentage,
          cumulativeTime: this.state.cumulativeWatchTime
        }
      };
    }

    // KAN-306 round 3: the initialLastTrackedTime floor is removed here too
    // (see the rewind branch above) -- always advance lastTrackedTime to
    // the current position.
    this.state.lastTrackedTime = this.state.currentVideoTime;

    // KAN-306 round 5: cumulativeWatchTime/videoWatchPercentage are no
    // longer computed independently here via their own +=-based
    // accumulator -- that was the SECOND, divergent "accrual engine"
    // implicated in the round-5 accrual-inflation bug (flagged as a
    // bug-class risk back in round 2). VideoProgressService now owns the
    // single interval-tracking engine (see watched-intervals.utils.ts) that
    // defeats the "replay a watched segment repeatedly" exploit; reading
    // its live, already-deduped coverage here means the displayed badge and
    // the persisted value can never diverge, and the display inherits the
    // same fix instead of needing its own.
    if (this.state.videoDuration > 0) {
      const liveCoverage = this.deps.videoProgressService.getCurrentCoverageSeconds();
      this.state.cumulativeWatchTime = Math.max(this.state.cumulativeWatchTime, liveCoverage);

      // Math.max enforces the monotonic invariant (the displayed % must
      // never decrease) as a floor against the previously known value.
      const computedPercentage = this.deps.videoProgressService.getCurrentWatchPercentageInt();
      this.state.videoWatchPercentage = Math.max(this.state.videoWatchPercentage, computedPercentage);

      if (!this.state.videoCompleted && this.state.videoWatchPercentage >= this.deps.getRequiredWatchPercentage()) {
        this.state.videoCompleted = true;
        // KAN-597: persist the crossing now - the periodic save is throttled
        // (up to 10s), and until the server confirms completion the footer can
        // only show a pending state, not Next.
        this.deps.videoProgressService.saveForCompletion();
      }
    }

    return {
      currentVideoTime: this.state.currentVideoTime,
      videoDuration: this.state.videoDuration,
      cumulativeWatchTime: this.state.cumulativeWatchTime,
      lastTrackedTime: this.state.lastTrackedTime,
      videoWatchPercentage: this.state.videoWatchPercentage,
      videoCompleted: this.state.videoCompleted,
      rewound,
      progressEvent: {
        currentTime: timeData.currentTime,
        duration: timeData.duration,
        percentage: this.state.videoWatchPercentage,
        cumulativeTime: this.state.cumulativeWatchTime
      }
    };
  }

  /** Load secure video URL based on videoId or direct URL. */
  loadSecureVideoUrl(): void {
    const videoId = this.deps.getVideoId();
    if (!videoId) return;

    const videoUrl = this.deps.getVideoUrl();

    if (videoUrl && (videoUrl.startsWith('http://') || videoUrl.startsWith('https://'))) {
      this.state.secureVideoUrl = videoUrl;
      this.state.isLoadingVideo = false;
      return;
    }

    const uuidRegex = /^[a-f0-9\-]{36}$/i;
    if (uuidRegex.test(videoId)) {
      this.state.secureVideoUrl = `https://iframe.mediadelivery.net/embed/${environment.bunnySharedLibraryId}/${videoId}?quality=720p`;
      this.state.isLoadingVideo = false;
      return;
    }

    const videoIdStr = String(videoId);
    if (videoIdStr.includes('bunny') || videoIdStr.includes('mediadelivery')) {
      const videoIdMatch = videoIdStr.match(/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i);
      if (videoIdMatch && videoIdMatch[1]) {
        this.state.secureVideoUrl = `https://iframe.mediadelivery.net/embed/${environment.bunnySharedLibraryId}/${videoIdMatch[1]}?quality=720p`;
        this.state.isLoadingVideo = false;
        return;
      }
    }

    this.state.isLoadingVideo = true;
    const subscription = this.deps.secureVideoService.getSecureVideoUrl(videoId).subscribe({
      next: (result) => {
        this.state.secureVideoUrl = result.playerUrl || result.signedUrl;
        this.state.isLoadingVideo = false;
        this.deps.onStateChanged?.();
      },
      error: () => {
        if (videoUrl) {
          this.state.secureVideoUrl = videoUrl;
        }
        this.state.isLoadingVideo = false;
        this.deps.onStateChanged?.();
      }
    });

    this.deps.subscriptions.push(subscription);
  }

  /**
   * Handle video element ready event.
   * Stores the element and starts progress tracking.
   */
  async onVideoReady(videoElement: HTMLVideoElement | HTMLIFrameElement): Promise<void> {
    if (videoElement instanceof HTMLVideoElement) {
      this.state.videoElement = videoElement;

      const moduleId = this.deps.getModuleId();
      const courseId = this.deps.getCourseId();

      if (moduleId && courseId) {
        const startPosition = this.state.lastTrackedTime || 0;
        await this.deps.videoProgressService.startTracking(
          videoElement,
          moduleId,
          courseId,
          startPosition,
          this.deps.getMode()
        );
      }

      videoElement.addEventListener('play', () => {
        this.state.isVideoPlaying = true;
      });

      videoElement.addEventListener('pause', () => {
        this.state.isVideoPlaying = false;
      });
    }
  }

  /**
   * Load a new video after module change.
   * Loads progress first, then loads the video URL.
   */
  async loadNewVideo(): Promise<void> {
    await this.loadExistingProgress();

    const videoId = this.deps.getVideoId();
    if (videoId) {
      this.loadSecureVideoUrl();
    } else {
      const videoUrl = this.deps.getVideoUrl();
      if (videoUrl) {
        this.state.secureVideoUrl = videoUrl;
      }
    }
  }

  /**
   * Handle Bunny video loaded event to detect and sync duration mismatches.
   */
  handleBunnyVideoLoaded(event: { duration: number }): void {
    const bunnyDuration = event.duration;

    if (!bunnyDuration || !isFinite(bunnyDuration) || bunnyDuration <= 0) {
      return;
    }

    if (!this.state.storedDuration) {
      return;
    }

    const ceiledBunnyDuration = Math.ceil(bunnyDuration);
    const ceiledStoredDuration = Math.ceil(this.state.storedDuration);

    if (Math.abs(ceiledStoredDuration - ceiledBunnyDuration) <= 0) {
      return;
    }

    const moduleId = this.deps.getModuleId();
    const courseId = this.deps.getCourseId();

    if (!moduleId || !courseId) return;

    const capturedModuleId = moduleId;

    this.deps.videoProgressService.updateVideoProgress(
      capturedModuleId,
      {
        courseId,
        lastPosition: this.state.lastTrackedTime || 0,
        furthestPosition: this.state.cumulativeWatchTime || 0,
        duration: Math.ceil(bunnyDuration),
        forceComplete: false
      }
    ).subscribe({
      next: () => {
        if (this.deps.getModuleId() === capturedModuleId) {
          this.state.storedDuration = Math.ceil(bunnyDuration);
          this.loadExistingProgress();
        }
      },
      error: () => {
        if (this.deps.getModuleId() === capturedModuleId) {
          this.state.storedDuration = Math.ceil(bunnyDuration);
        }
      }
    });
  }
}
